<template>
    <dropdown
        ref="dropdown"
        class="site-header__accounts"
        :class="{'site-header__accounts--loggedin': hasUser && user.loggedin }"
        hover="small"
        :align="align"
        data-navigation="Account"
    >
        <slot></slot>
        <dropdown-content v-if="hasUser" :id="menuId">
            <dropdown-back class="hidden-small-gte" aria-label="Main Menu"></dropdown-back>

            <template v-if="user.loggedin">
                <dropdown-row :gutterless="true">
                    <dropdown-col :span="12" :sm="6">
                        <dropdown-headline :href="defaultListUrl" data-navigation="Account > Wishlist - Individual">
                            Your {{ 'List' | pluralize(user.listsCount) }}
                        </dropdown-headline>

                        <dropdown-group v-if="user.listsCount" class="fs-mask">
                            <dropdown-item v-for="list in visibleLists" :key="list.hash" :href="list.url" borderless data-navigation="Account > Wishlist - Individual">
                                {{ list.name }} <small v-if="list.default_list === 1">(default)</small>
                            </dropdown-item>

                            <dropdown-item v-if="user.listsCount > visibleListsLimit" :href="defaultListUrl" borderless data-navigation="Account > Wishlist - Individual">
                                See more...
                            </dropdown-item>
                        </dropdown-group>

                        <dropdown-group v-if="user.listsCount < maxList">
                            <dropdown-item :href="createListUrl" borderless data-navigation="Account > Create a new list">
                                Create a new list
                            </dropdown-item>
                        </dropdown-group>
                    </dropdown-col>

                    <dropdown-col :span="12" :sm="6">
                        <dropdown-headline href="/myaccount/" data-navigation="Account">
                            Your Account
                        </dropdown-headline>

                        <dropdown-item href="/myaccount/update_account/" borderless data-navigation="Account > Your Account Info">
                            Your Account Info
                        </dropdown-item>

                        <dropdown-item href="/myaccount/accounts/" borderless data-navigation="Account > Your Address &amp; Payment Info">
                            Your Address &amp; Payment Info
                        </dropdown-item>

                        <dropdown-item href="/myaccount/prefs/" borderless data-navigation="Account > Your Contact Preferences">
                            Your Contact Preferences
                        </dropdown-item>

                        <dropdown-item href="/myaccount/history/" borderless data-navigation="Account > Your Order History">
                            Your Order History
                        </dropdown-item>

                        <dropdown-item href="/tracking/" borderless data-navigation="Tracking">
                            Check Order Status
                        </dropdown-item>

                        <dropdown-item href="/auth/signout" data-navigation="Account > Log out">
                            <span class="site-header__logout-container">
                                <span v-if="logoutName" class="site-header__logout-name"> Not <strong v-html="logoutName" class="fs-mask"></strong>? </span> Log out
                            </span>
                        </dropdown-item>
                    </dropdown-col>
                </dropdown-row>
            </template>
            <template v-else>
                <dropdown-headline :href="defaultListUrl" class="hidden-small-gte" data-navigation="Account">
                    Your Account
                </dropdown-headline>

                <div class="site-header__login-block" borderless>
                    <a href="/auth/signin" class="btn--blue btn--med site-header__btn--login" data-navigation="Account > Log in" data-testid="login">Log in</a>
                    <span class="site-header__signup">New here? <a href="/auth/signup" data-navigation="Account > Create your account">Create your account</a></span>
                </div>

                <dropdown-item href="/store/wishlist" borderless data-navigation="Account > Create a list">
                    Create a list
                </dropdown-item>

                <dropdown-item href="/tracking/" borderless data-navigation="Tracking">
                    Check Your Order Status
                </dropdown-item>
            </template>
        </dropdown-content>
    </dropdown>
</template>

<script>
    let accountMenuCount = 0;

    export default {
        data() {
            accountMenuCount += 1;
            return {
                lists: [],
                url: '/store/wishlist',
                menuId: `site-header-account-menu-${accountMenuCount}`,
            };
        },
        props: {
            maxList: {
                default: 50,
            },
            align: {
                required: false,
                default: '',
            },
            loggedin: {
                default: false,
                type: Boolean,
            },
            listsJson: {
                default: '',
                type: String,
            },
            source: {
                default: '',
                type: String,
            },
            userFirstname: {
                default: '',
                type: String,
            },
            visibleListsLimit: {
                default: 5,
                type: Number,
            },

            userData: {
                type: String,
                required: false,
                default: '',
            },
        },
        created() {
            events.$on('wishlistUpdate', this.updateWishlistLists);
        },
        computed: {
            user() {
                return this.$store.state.user.summary;
            },
            hasUser() {
                return typeof this.user === 'object';
            },
            createListUrl() {
                return `${this.defaultListUrl}?a=create_list`;
            },
            defaultList() {
                let defaultList = false;
                if (this.user.listsCount) {
                    defaultList = this.user.lists[0];
                    this.lists.forEach((list) => {
                        if (Object.prototype.hasOwnProperty.call(list, 'default_list') && list.default_list === 1) {
                            defaultList = list;
                        }
                    });
                }
                return defaultList;
            },
            defaultListUrlHash() {
                return (this.defaultList ? `/${this.defaultList.hash}` : '');
            },
            defaultListUrl() {
                return this.url + this.defaultListUrlHash;
            },
            editListsUrl() {
                return `${this.defaultListUrl}?a=edit_lists`;
            },
            logoutName() {
                return (this.user.firstName ? `${this.user.firstName}` : '');
            },
            visibleLists() {
                return this.user.lists.slice(0, this.visibleListsLimit);
            },
        },
        filters: {
            pluralize(value, length) {
                let localValue = value;
                if (!localValue) {
                    return '';
                }
                localValue = localValue.toString();
                return localValue + (length === 1 ? '' : 's');
            },
        },
        watch: {
            hasUser() {
                this.syncMenuToggle();
            },
        },
        mounted() {
            const dropdown = this.$refs.dropdown;
            this.menuToggle = this.$el.querySelector(':scope > .site-header__account-menu-toggle');
            if (!dropdown || !this.menuToggle) {
                return;
            }

            this.syncMenuToggle();

            this.unwatchExpanded = dropdown.$watch('expanded', this.syncMenuToggle);

            this.menuToggle.addEventListener('click', this.handleToggleClick);

            this.$el.addEventListener('keydown', this.handleMenuKeydown, true);
            this.$el.addEventListener('focusout', this.handleMenuFocusOut);
        },
        beforeDestroy() {
            if (this.unwatchExpanded) {
                this.unwatchExpanded();
            }
            if (this.menuToggle) {
                this.menuToggle.removeEventListener('click', this.handleToggleClick);
            }
            this.$el.removeEventListener('keydown', this.handleMenuKeydown, true);
            this.$el.removeEventListener('focusout', this.handleMenuFocusOut);
        },
        methods: {
            syncMenuToggle() {
                if (!this.menuToggle) {
                    return;
                }
                this.menuToggle.setAttribute('aria-expanded', this.menuIsOpen() ? 'true' : 'false');
                if (this.hasUser) {
                    this.menuToggle.setAttribute('aria-controls', this.menuId);
                } else {
                    this.menuToggle.removeAttribute('aria-controls');
                }
            },
            handleToggleClick(e) {
                e.stopPropagation();
                this.toggleMenu();
            },
            menuIsOpen() {
                return Boolean(this.$refs.dropdown && this.$refs.dropdown.expanded);
            },
            openMenu() {
                if (this.$refs.dropdown) {
                    this.$refs.dropdown.show();
                }
            },
            closeMenu(focusToggle = false) {
                if (this.$refs.dropdown) {
                    this.$refs.dropdown.onOut();
                }
                if (focusToggle && this.menuToggle) {
                    this.menuToggle.focus();
                }
            },
            toggleMenu() {
                if (this.menuIsOpen()) {
                    this.closeMenu();
                } else {
                    this.openMenu();
                }
            },
            focusFirstMenuItem() {
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        const content = this.$el.querySelector('.v-dropdown-content');
                        const items = content ? content.querySelectorAll('a[href], button:not([disabled])') : [];
                        const firstItem = Array.from(items).find(item => item.getClientRects().length > 0);
                        if (firstItem) {
                            firstItem.focus();
                        }
                    });
                });
            },
            handleMenuKeydown(e) {
                const onToggle = e.target === this.menuToggle;
                const onAccountLink = e.target === this.$el.querySelector(':scope > .v-dropdown-toggle');
                if (e.key === 'Escape') {
                    if (!this.menuIsOpen()) {
                        return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeMenu(true);
                    return;
                }

                if (!onToggle && !onAccountLink) {
                    return;
                }

                if (onToggle && (e.key === 'Enter' || e.key === ' ')) {
                    e.stopPropagation();
                    return;
                }

                if (e.key === 'ArrowDown' || (onAccountLink && e.key === ' ')) {
                    e.preventDefault();
                    this.openMenu();
                    this.focusFirstMenuItem();
                    return;
                }

                if (e.key === 'Tab' && !e.shiftKey) {
                    if (onAccountLink && this.menuToggle) {
                        e.preventDefault();
                        this.menuToggle.focus();
                    } else if (onToggle && this.menuIsOpen()) {
                        e.preventDefault();
                        e.stopPropagation();
                        this.focusFirstMenuItem();
                    }
                }
            },
            handleMenuFocusOut(e) {
                if (!this.menuIsOpen() || (e.relatedTarget && this.$el.contains(e.relatedTarget))) {
                    return;
                }
                if (this.$el.matches(':hover')) {
                    return;
                }
                this.closeMenu();
            },
        },
    };
</script>
